import {RegisterOptions, useFormContext} from "react-hook-form";

import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Input} from "@/components/input/Input.tsx";
import {InputGroup} from "@/components/inputGroup/InputGroup.tsx";
import {InputGroupProps} from "@/components/inputGroup/InputGroup.types.ts";
import {getFormError} from "@/lib/form/form.utils.ts";
import {getRequiredError} from "@/pages/checks/components/form/constants.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

export type InputGroupFieldProps = FieldProps &
  Pick<
    InputGroupProps,
    | "startElement"
    | "startElementProps"
    | "endElement"
    | "endElementProps"
    | "startAddon"
    | "startAddonProps"
    | "endAddon"
    | "endAddonProps"
    | "startOffset"
    | "endOffset"
  > & {
    maxLength?: number;
    options?: RegisterOptions;
    hideErrorMessage?: boolean;
  };

export const InputGroupField = ({
  id,
  name,
  label,
  className,
  tooltip,
  supportLink,
  helpText,
  required,
  placeholder,
  maxLength,
  disabled = false,
  autoFocus = false,
  hideErrorMessage = false,
  options = {},
  labelIcon,
  startElement,
  startElementProps,
  endElement,
  endElementProps,
  startAddon,
  startAddonProps,
  endAddon,
  endAddonProps,
  startOffset,
  endOffset,
}: InputGroupFieldProps) => {
  const {
    register,
    formState: {errors},
  } = useFormContext();
  const fieldError = getFormError(name, errors);
  if (required) {
    options.required = getRequiredError(name);
  }

  return (
    <div className={className}>
      {label && (
        <FormLabel helpText={tooltip} forId={id || name} required={required} icon={labelIcon}>
          {label}
        </FormLabel>
      )}
      <InputGroup
        startElement={startElement}
        startElementProps={startElementProps}
        endElement={endElement}
        endElementProps={endElementProps}
        startAddon={startAddon}
        startAddonProps={startAddonProps}
        endAddon={endAddon}
        endAddonProps={endAddonProps}
        startOffset={startOffset}
        endOffset={endOffset}
        disabled={disabled}
        aria-invalid={!!fieldError}
      >
        <Input
          id={id}
          maxLength={maxLength}
          placeholder={placeholder}
          autoFocus={autoFocus}
          {...register(name, options)}
          type="text"
          autoComplete="off"
        />
      </InputGroup>
      <FormSubtext
        subText={helpText}
        supportLink={supportLink}
        error={hideErrorMessage ? "" : fieldError?.toString()}
      />
    </div>
  );
};
